import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiFile } from 'react-icons/all';
import { FiX } from 'react-icons/fi';
import { Heading, Input } from '~/components';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import { useSinister } from '~/hooks/useSinisters/useSinisters';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersInsurerForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const orcamentoRef = useRef<HTMLInputElement | null>(null);
  const notaRemessaRef = useRef<HTMLInputElement | null>(null);

  const [nota_remessa, setSelectedNotaRemessa] = useState<any>();
  const [nomeNotaRemessa, setNomeNotaRemessa] = useState<string | undefined>(
    '',
  );
  const [orcamento, setSelectedOrcamento] = useState<any>();
  const [nomeOrcamento, setNomeOrcamento] = useState<string | undefined>('');
  const { data, isLoading, refetch } = useSinister(sinisterId);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNomeOrcamento('');
    setSelectedOrcamento(null);
    setNomeNotaRemessa('');
    setSelectedNotaRemessa(null);
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    !(nota_remessa || nomeNotaRemessa) &&
      setError('nota_remessa', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = new FormData();
    if (nota_remessa) {
      form.append('nota_de_remessa', nota_remessa);
    }
    if (orcamento) {
      form.append('orcamento_de_reparo', orcamento);
    }

    setFetching(true);

    const request = apiSinister.put(`enviar-a-seguradora/${data?.id}/`, form);

    request
      .then(() => {
        toast({
          title: `Enviado à seguradora`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries('sinisterLog');
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
        queryClient.invalidateQueries(['sinisterDocuments', sinisterId]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            console.log(err[0]);
            toast({
              title: `Erros ao enviar à seguradora`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao enviar à seguradora`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }
  const handleNotaRemessaChange = (_file: File | null) => {
    setSelectedNotaRemessa(_file);
    setNomeNotaRemessa(_file ? _file.name : '');
    clearErrors('nota_remessa');
  };
  const clearNotaRemessa = () => {
    setSelectedNotaRemessa(null);
    setNomeNotaRemessa('');
    setValue('nota_remessa', null);
  };

  const handleOrcamentoChange = (_file: File | null) => {
    setSelectedOrcamento(_file);
    setNomeOrcamento(_file ? _file.name : '');
    clearErrors('orcamento');
  };
  const clearOrcamento = () => {
    setSelectedOrcamento(null);
    setNomeOrcamento('');
    setValue('orcamento', null);
  };

  return (
    <>
      <MenuItem onClick={handleOpen}>Enviar à seguradora</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Resumo de envio à seguradora</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} isAdmin />
                <Heading>Uploads</Heading>
                {/* nota remessa */}
                <FormControl isInvalid={errors.nota_remessa}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Nota de remessa *
                  </FormLabel>
                  <InputGroup width="60%">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(nota_remessa || nomeNotaRemessa) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearNotaRemessa}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleNotaRemessaChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, .jpg"
                      ref={notaRemessaRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        notaRemessaRef.current
                          ? notaRemessaRef.current.click()
                          : ''
                      }
                      value={nomeNotaRemessa || ''}
                      name="boletim"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.nota_remessa}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
                {/* orcamento */}
                <FormControl isInvalid={errors.orcamento}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Orçamento de reparo
                  </FormLabel>
                  <InputGroup width="60%">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(orcamento || nomeOrcamento) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearOrcamento}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleOrcamentoChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, .jpg"
                      ref={orcamentoRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        orcamentoRef.current ? orcamentoRef.current.click() : ''
                      }
                      value={nomeOrcamento || ''}
                      name="boletim"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.orcamento}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Enviar à seguradora{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
